import type { PlayerId } from "@/types/branded";
import type { MatchId } from "../types";
import type { Edge } from "../types/edge";
import type { SwissNode } from "../types/node";
import type { SwissParticipantStats } from "../types/participantStat";

import type { SwissMatchFinishedEvent } from "./swiss.machine";

export const enum Points {
  Win = 3,
  Draw = 1,
  Loss = 0,
}

type Pairing = {
  teamA: PlayerId[];
  teamB: PlayerId[];
};

export const calculateNextRound = (
  standings: Map<PlayerId, SwissParticipantStats>,
  round: number,
) => {
  const sortedPlayers = Array.from(standings.values()).sort((a, b) => b.points - a.points);
  const { byePlayer, remaining } = extractByePlayer(sortedPlayers);
  const pairings = pairPool(remaining);

  const nodes: SwissNode[] = [];
  const edges: Edge[] = [];

  if (byePlayer) {
    const byeMatchId = `r${round}-bye` as MatchId;
    nodes.push({ id: byeMatchId, round });
    edges.push({ kind: "seed", player: byePlayer.id, to: byeMatchId, slot: 0 });
    edges.push({ kind: "seed", player: null, to: byeMatchId, slot: 1 });
  }

  // 4. Create Match Nodes
  pairings.forEach((p, idx) => {
    const matchId = `r${round}-m${idx}` as MatchId;
    nodes.push({ id: matchId, round });
    edges.push({ kind: "seed", player: p.teamA[0]!, to: matchId, slot: 0 });
    edges.push({ kind: "seed", player: p.teamB[0]!, to: matchId, slot: 1 });
  });

  return { nodes, edges };
};

export const updatePlayerStats = (
  standings: Map<PlayerId, SwissParticipantStats>,
  result: SwissMatchFinishedEvent,
): Map<PlayerId, SwissParticipantStats> => {
  const { winnerId, players } = result;
  const newStandings = new Map(standings);

  const [p1Id, p2Id] = players;
  if (!p1Id || !p2Id) {
    const byePlayerId = p1Id || p2Id;
    if (byePlayerId) {
      const stats = { ...newStandings.get(byePlayerId)! };
      stats.points += 1;
      stats.wins += 1;
      stats.hadBye = true;
      newStandings.set(byePlayerId, stats);
    }
    return newStandings;
  }

  const p1 = { ...newStandings.get(p1Id)! };
  const p2 = { ...newStandings.get(p2Id)! };

  p1.opponents.push(p2Id);
  p2.opponents.push(p1Id);

  if (winnerId === p1Id) {
    p1.wins += 1;
    p1.points += Points.Win;
    p2.losses += 1;
    p2.points += Points.Loss;
  } else if (winnerId === p2Id) {
    p2.wins += 1;
    p2.points += Points.Win;
    p1.losses += 1;
    p1.points += Points.Loss;
  } else if (winnerId === "draw") {
    p1.draws += 1;
    p2.draws += 1;
    p1.points += Points.Draw;
    p2.points += Points.Draw;
  } else {
    throw new Error("Invalid winnerId in MATCH_FINISHED event");
  }

  newStandings.set(p1Id, p1);
  newStandings.set(p2Id, p2);

  return newStandings;
};

export const createInitialStandings = (players: PlayerId[]): Map<PlayerId, SwissParticipantStats> =>
  new Map<PlayerId, SwissParticipantStats>(
    players.map((p) => [
      p,
      {
        id: p,
        points: 0,
        wins: 0,
        losses: 0,
        draws: 0,
        opponents: [],
        hadBye: false,
        buchholz: 0,
        sonnebornBerger: 0,
      },
    ]),
  );

const canPlay = (a: SwissParticipantStats, b: SwissParticipantStats): boolean => {
  return !a.opponents.includes(b.id);
};

/**
 * Finds a player to receive a Bye if the pool is odd.
 * Usually the lowest-ranked player who hasn't had one.
 */
export const extractByePlayer = (
  players: SwissParticipantStats[],
): { byePlayer: SwissParticipantStats | null; remaining: SwissParticipantStats[] } => {
  if (players.length % 2 === 0) return { byePlayer: null, remaining: players };

  // Search from the bottom up for someone who hasn't had a bye
  for (let i = players.length - 1; i >= 0; i--) {
    if (!players[i]!.hadBye) {
      const remaining = [...players];
      const [byePlayer] = remaining.splice(i, 1);
      return { byePlayer: byePlayer!, remaining };
    }
  }

  // Fallback: just take the last person if everyone has had a bye
  // (rare, but prevents crashes in small tournaments)
  const remaining = [...players];
  const byePlayer = remaining.pop()!;
  return { byePlayer, remaining };
};

export const pairPool = (players: SwissParticipantStats[], maxIterations = 20_000): Pairing[] => {
  let iterations = 0;
  let best: Pairing[] | null = null;

  // We sort candidates by score proximity to the 'base' player.
  // This naturally handles "floaters" by checking peers first,
  // then checking the next score bracket down.
  const getSortedCandidates = (
    base: SwissParticipantStats,
    candidates: SwissParticipantStats[],
  ) => {
    return [...candidates].sort(
      (a, b) => Math.abs(base.points - a.points) - Math.abs(base.points - b.points),
    );
  };

  const backtrack = (remaining: SwissParticipantStats[], current: Pairing[]) => {
    if (iterations++ > maxIterations || best) return;

    if (remaining.length === 0) {
      best = current;
      return;
    }

    const [first, ...rest] = remaining;
    if (!first) return;
    const candidates = getSortedCandidates(first, rest);

    for (const opponent of candidates) {
      if (!canPlay(first, opponent)) continue;

      const nextRemaining = rest.filter((p) => p.id !== opponent.id);
      backtrack(nextRemaining, [...current, { teamA: [first.id], teamB: [opponent.id] }]);

      if (best) return;
    }
  };

  backtrack(players, []);

  if (!best) {
    throw new Error("Swiss Pairing Exhaustion: No valid pairings possible without rematches.");
  }

  return best;
};
